import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { getUserColumns } from "./columns";
import UserActionsDialog from "../../../../components/ModaleUser/UserActionsDialog";
import DashboardTableLayout from "../../../../components/Dashboard/DashboardTableLayout";
import { useGetUsersQuery } from "../../../../Services/modules/users";
import { useDebounce } from "../../../../hooks/useDebounce";
import DataTable from "../../../../components/Dashboard/DataTable";
import { useSelector } from "react-redux";
import { whitelabelListSelector } from "../../../../store/Whitelabel/WhitelabelList";

export default function UsersPage() {
  const { selectedWhiteLabel } = useSelector(whitelabelListSelector);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openUserActionsDialog, setOpenUserActionsDialog] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const queryArgs = {
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    search: debouncedSearch,
    sorting,
    whiteLabel: selectedWhiteLabel,
  };

  const { data, isLoading } = useGetUsersQuery(queryArgs);
  const users =
    data?.users.filter((user) => {
      if (
        selectedWhiteLabel === "All whitelabels" ||
        selectedWhiteLabel === null
      ) {
        return data?.users;
      }

      return user.whiteLabel === selectedWhiteLabel;
    }) || [];

  const pageInfo = data?.pageInfo || {
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  };

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debouncedSearch]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [selectedWhiteLabel]);

  const handleUserActions = useCallback(() => {
    setOpenUserActionsDialog(true);
  }, []);

  const columns = useMemo(
    () =>
      getUserColumns({
        onSelectUser: setSelectedUser,
        onUserActions: handleUserActions,
      }),
    [handleUserActions]
  );

  const table = useReactTable({
    data: users,
    columns,
    pageCount: pageInfo.totalPages,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <DashboardTableLayout
      searchValue={search}
      onSearchChange={setSearch}
      pageSize={pagination.pageSize}
      onPageSizeChange={(newSize) =>
        setPagination((p) => ({ ...p, pageSize: newSize, pageIndex: 0 }))
      }
    >
      <DataTable
        table={table}
        columns={columns}
        data={users}
        isLoading={isLoading}
        pageInfo={pageInfo}
        pagination={pagination}
      />
      <UserActionsDialog
        user={selectedUser}
        open={openUserActionsDialog}
        onOpenChange={setOpenUserActionsDialog}
        queryArgs={queryArgs}
      />
    </DashboardTableLayout>
  );
}
